import type { TextLine } from "@chuzi/shared/types";

interface ActiveSource {
  source: AudioBufferSourceNode;
  gain: GainNode;
  lineId: string;
}

/**
 * Web Audio–based cue engine. All buffers must be pre-decoded before playback.
 */
export class SoundCueEngine {
  private ctx: AudioContext | null = null;
  private buffers: Map<string, AudioBuffer> = new Map();
  private active: ActiveSource[] = [];
  private startedLines = new Set<string>();

  setBuffers(buffers: Map<string, AudioBuffer>) {
    this.buffers = buffers;
  }

  private ensureContext() {
    if (!this.ctx) {
      this.ctx = new AudioContext();
    }
    if (this.ctx.state === "suspended") {
      void this.ctx.resume();
    }
    return this.ctx;
  }

  resetScene() {
    this.stopAll();
    this.startedLines.clear();
  }

  stopAll() {
    for (const { source, gain } of this.active) {
      try {
        source.stop();
        source.disconnect();
        gain.disconnect();
      } catch {
        /* already stopped */
      }
    }
    this.active = [];
  }

  dispose() {
    this.stopAll();
    void this.ctx?.close();
    this.ctx = null;
    this.startedLines.clear();
  }

  sync(
    lines: TextLine[],
    playheadMs: number,
    isPlaying: boolean,
    urlByMediaId: Map<string, string>,
  ) {
    if (!isPlaying) {
      this.stopAll();
      return;
    }

    const ctx = this.ensureContext();

    for (const line of lines) {
      if (line.type !== "sound" || !line.media_id) continue;

      const buffer =
        this.buffers.get(line.media_id) ??
        this.buffers.get(urlByMediaId.get(line.media_id) ?? "");
      if (!buffer) continue;

      const start = line.appear_at_ms;
      const fadeInEnd = start + line.fade_in_ms;
      const holdEnd = fadeInEnd + line.duration_ms;
      const fadeOutEnd = holdEnd + line.fade_out_ms;
      const inWindow = playheadMs >= start && playheadMs < fadeOutEnd;

      if (!inWindow) {
        if (this.startedLines.has(line.id)) {
          this.stopLine(line.id);
        }
        continue;
      }

      let volume = 1;
      if (playheadMs < fadeInEnd && line.fade_in_ms > 0) {
        volume = (playheadMs - start) / line.fade_in_ms;
      } else if (playheadMs >= holdEnd && line.fade_out_ms > 0) {
        volume = 1 - (playheadMs - holdEnd) / line.fade_out_ms;
      }
      volume = Math.max(0, Math.min(1, volume));

      const existing = this.active.find((a) => a.lineId === line.id);
      if (existing) {
        existing.gain.gain.value = volume;
        continue;
      }

      if (this.startedLines.has(line.id)) continue;

      const source = ctx.createBufferSource();
      source.buffer = buffer;
      const gain = ctx.createGain();
      gain.gain.value = volume;
      source.connect(gain);
      gain.connect(ctx.destination);

      const offsetSec = Math.max(0, (playheadMs - start) / 1000);
      try {
        source.start(0, Math.min(offsetSec, buffer.duration));
        this.active.push({ source, gain, lineId: line.id });
        this.startedLines.add(line.id);
        source.onended = () => this.stopLine(line.id);
      } catch {
        /* ignore scheduling edge cases */
      }
    }
  }

  private stopLine(lineId: string) {
    const idx = this.active.findIndex((a) => a.lineId === lineId);
    if (idx < 0) return;
    const { source, gain } = this.active[idx];
    try {
      source.stop();
      source.disconnect();
      gain.disconnect();
    } catch {
      /* noop */
    }
    this.active.splice(idx, 1);
    this.startedLines.delete(lineId);
  }
}
